import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import AuditBackground from './AuditBackground';
import WelcomeScreen from './WelcomeScreen';
import InputsScreen from './InputsScreen';
import QuestionsScreen from './QuestionsScreen';
import GateScreen from './GateScreen';
import ResultsScreen from './ResultsScreen';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const pageTransition = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
};

const ROICalculator = () => {
  const navigate = useNavigate();
  const { resultId } = useParams();
  const [screen, setScreen] = useState(resultId ? 'loading' : 'welcome');
  const [businessInputs, setBusinessInputs] = useState({
    monthlyOrders: null, aov: null, country: '', industry: '',
    dataCollectionPct: 60, repeatSalesPct: 40,
  });
  const [answers, setAnswers] = useState({});
  const [scores, setScores] = useState(null);
  const [gateData, setGateData] = useState({});
  const [savedResultId, setSavedResultId] = useState(resultId || null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = 'Xeno — Audit';
    return () => { document.title = 'Xeno'; };
  }, [screen]);

  // Load shared results if resultId is in URL
  useEffect(() => {
    if (!resultId) return;
    const loadResults = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/results/${resultId}`);
        const data = await res.json();
        if (data.ok && data.data) {
          const d = data.data;
          setBusinessInputs(d.businessInputs);
          setAnswers(d.answers);
          setScores(d.scores);
          setGateData({ email: d.email, companyName: d.companyName });
          setSavedResultId(resultId);
          setScreen('results');
        } else {
          setScreen('welcome');
        }
      } catch {
        setScreen('welcome');
      }
    };
    loadResults();
  }, [resultId]);

  const handleRestart = () => {
    setScreen('welcome');
    setBusinessInputs({ monthlyOrders: null, aov: null, country: '', industry: '', dataCollectionPct: 60, repeatSalesPct: 40 });
    setAnswers({});
    setScores(null);
    setGateData({});
    setSavedResultId(null);
    navigate('/roi-calculator');
  };

  return (
    <div className="min-h-screen relative" style={{ background: '#F8FAFC' }}>
      <AuditBackground />
      <nav
        className="sticky top-0 z-50 h-[68px] flex items-center px-6"
        style={{ background: 'rgba(248,250,252,0.85)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(59,130,246,0.08)' }}
      >
        <div className="max-w-[1100px] w-full mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center" data-testid="nav-logo-link">
            <img src="https://cdn.prod.website-files.com/620353a026ae70e21288308a/69e0a8442fde5f7cc3b2d3c6_newlogoxeno-blue11.png" alt="Xeno" className="h-6" />
          </a>
          <div className="flex items-center gap-4">
            <span className="text-base font-semibold text-[#0f172a] hidden sm:block">CRM & Loyalty Maturity Audit</span>
            <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-sm font-medium text-[#94a3b8] hover:text-[#0f172a] transition-colors cursor-pointer" data-testid="nav-back-button">
              <ArrowLeft className="w-4 h-4" />
            </button>
          </div>
        </div>
      </nav>

      <main className="relative z-10">
        <div className={screen === 'welcome' ? '' : 'max-w-[1100px] mx-auto px-5 pt-10 pb-16'}>
          <AnimatePresence mode="wait">
            {screen === 'loading' && (
              <motion.div key="loading" {...pageTransition}>
                <div className="flex items-center justify-center min-h-[60vh]">
                  <p className="text-[#64748b] text-sm">Loading results...</p>
                </div>
              </motion.div>
            )}
            {screen === 'welcome' && (
              <motion.div key="welcome" {...pageTransition}>
                <WelcomeScreen onStart={() => setScreen('inputs')} />
              </motion.div>
            )}
            {screen === 'inputs' && (
              <motion.div key="inputs" {...pageTransition}>
                <InputsScreen onNext={() => setScreen('questions')} onBack={() => setScreen('welcome')} businessInputs={businessInputs} setBusinessInputs={setBusinessInputs} />
              </motion.div>
            )}
            {screen === 'questions' && (
              <motion.div key="questions" {...pageTransition}>
                <QuestionsScreen onNext={(computedScores) => { setScores(computedScores); setScreen('gate'); }} onBack={() => setScreen('inputs')} answers={answers} setAnswers={setAnswers} />
              </motion.div>
            )}
            {screen === 'gate' && (
              <motion.div key="gate" {...pageTransition}>
                <GateScreen
                  onNext={(resId) => { if (resId) setSavedResultId(resId); setScreen('results'); }}
                  onBack={() => setScreen('questions')}
                  gateData={gateData} setGateData={setGateData}
                  scores={scores} answers={answers} businessInputs={businessInputs}
                />
              </motion.div>
            )}
            {screen === 'results' && (
              <motion.div key="results" {...pageTransition}>
                <ResultsScreen answers={answers} scores={scores} inputData={businessInputs} gateData={gateData} onRestart={handleRestart} resultId={savedResultId} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default ROICalculator;
